import React from "react";
import { graphql, Link } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import PortableText from "../components/Blog/portableText";

import Layout from "../components/Layout";
import SearchEngineOptimization from "../components/SEO";
import Testimonials from "../components/Repeating/Testimonials";
import About from "../components/Repeating/About";
import RecentPosts from "../components/Repeating/RecentPosts";
import CallToAction from "../components/Repeating/CTA";
import ButtonSolid from "../components/Button/ButtonSolid";

import largePlus from "../images/0.0 Repeating Modules/Plus icon large.svg";
import smallPlus from "../images/0.0 Repeating Modules/Plus icon small.svg";

export const query = graphql`
  query CityTemplateQuery($id: String!) {
    city: sanityCities(id: { eq: $id }) {
      id
      title
      seoTitle
      metaDescription
      openGraphImage {
        asset {
          url
        }
      }
      twitterCardImage {
        asset {
          url
        }
      }
      state
      whyWeLoveImage {
        asset {
          gatsbyImageData(layout: CONSTRAINED, width: 1200)
        }
      }
      _rawWhyWeLoveText
    }
  }
`;

const Template = ({ data }) => {
  return (
    <Layout>
      <SearchEngineOptimization
        title={data.city.seoTitle}
        description={data.city.metaDescription}
        openGraphImage={
          data.city.openGraphImage && data.city.openGraphImage.asset.url
        }
        twitterOpenGraphImage={
          data.city.twitterCardImage && data.city.twitterCardImage.asset.url
        }
      />

      <section className="relative mb-14 pb-12 pt-64 md:mb-18 md:pb-60">
        <div className="absolute left-0 top-0 hidden h-full w-full md:block">
          <StaticImage
            src="../images/1.0 Homepage/1.0 Hero.jpg"
            loading="eager"
            className="h-full"
            imgClassName="object-top"
          />
        </div>
        <div className="absolute left-0 top-0 h-full w-full md:hidden">
          <StaticImage
            src="../images/1.0 Homepage/1.0 Hero mobile.jpg"
            loading="eager"
            className="h-full"
            imgClassName="object-top"
          />
        </div>

        <div className="absolute right-0 -bottom-18">
          <StaticImage
            src="../images/1.0 Homepage/1.1 Cross Pattern.png"
            loading="lazy"
            width={207}
            className="md: z-10 h-full w-[109px] md:w-[209px]"
          />
        </div>

        <div className="gradient-blend absolute bottom-0 h-52 w-full"></div>
        <div className="container">
          <div className="relative max-w-[788px]">
            <div className="mb-7 font-heading text-mobile-7xl font-semibold text-gray-100 md:mb-5 md:text-7xl">
              <span className="text-white">Planning</span> today for tomorrow’s{" "}
              <span className="text-white">success.</span>
            </div>
            <p className="mb-14 text-2xl text-white md:mb-7 md:text-[1.375rem] md:leading-[2rem]">
              Your DC CPA firm since 1978.
            </p>
            <div className="flex flex-wrap items-center space-y-4 md:flex-nowrap md:space-y-0 md:space-x-4">
              <ButtonSolid
                href="/business-cpa-services/"
                text="Business"
                className="w-full md:w-auto"
              />
              <ButtonSolid
                href="/cpa-services-for-individuals/"
                altStyle={2}
                text="Personal"
                className="w-full md:w-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative mb-24 md:mb-32">
        <div className="container">
          <div className="grid gap-y-20 md:grid-cols-2 md:gap-x-10 lg:gap-x-20">
            <div className="order-2 md:order-1 md:pt-24 md:pb-24">
              <h1>Your {data.city.title} CPA Firm Since 1978</h1>
              <p className="mb-0 md:text-xl">
                You need the right people in your corner, especially when it
                comes to your money. At DeBlanc + Murphy, we believe in helping
                you with today’s tax and accounting needs while keeping an eye
                toward the future. Your goals are our goals.
              </p>
            </div>
            <div className="relative order-1 hidden md:order-2 md:block md:h-full">
              <img
                src={largePlus}
                alt="Large plus sign"
                width={95}
                className="absolute z-10 w-[54px] md:-bottom-12 md:-left-20 md:w-[95px]"
              />
            </div>
            <div className="relative h-full md:absolute md:right-0 md:w-[50vw]">
              <StaticImage
                src="../images/1.0 Homepage/2.0 intro.jpg"
                loading="lazy"
                width={680}
                className="h-full w-full rounded-tl-6xl rounded-br-6xl"
              />

              <img
                src={largePlus}
                alt="Large plus sign"
                width={95}
                className="absolute -bottom-8 left-4 z-10 w-[54px] md:-bottom-12 md:-left-12 md:hidden md:w-[95px]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mb-24 md:mb-40">
        <div className="container">
          <header className="mb-14 md:mb-20">
            <h2>What You Get</h2>
          </header>

          <div className="grid items-center justify-between gap-y-6 md:flex md:space-x-10 lg:space-x-16">
            <div>
              <h3 className="font-normal">The Best Tools & Advice</h3>
              <p className="mb-0">
                We put sophisticated tax planning tools and expert advice to
                work for you so you get the most out of life.
              </p>
            </div>

            <img
              src={smallPlus}
              width={45}
              alt="Small plus sign"
              className="mx-auto md:mx-0"
            />

            <div>
              <h3 className="font-normal">Your Problems, Solved</h3>
              <p className="mb-0">
                We optimize your books and take the stress out of taxes,
                compliance, and financial issues impacting your business.
              </p>
            </div>

            <img
              src={smallPlus}
              width={45}
              alt="Small plus sign"
              className="mx-auto md:mx-0"
            />

            <div>
              <h3 className="font-normal">A Better Way to Pay</h3>
              <p className="mb-0">
                We’re different from other{" "}
                {data.city.state
                  ? data.city.title + ", " + data.city.state
                  : data.city.title}{" "}
                CPA firms. You pay for the value we provide, not the hours we
                put in.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container">
          <div className="grid md:grid-cols-2">
            <div className="-mx-4 md:mx-0 md:min-h-[847px]">
              <div className="h-full overflow-hidden rounded-tr-6xl md:absolute md:left-0 md:w-[50vw]">
                <StaticImage
                  src="../images/1.0 Homepage/3.0 business.jpg"
                  loading="lazy"
                  width={660}
                  className="h-full w-full"
                />
              </div>
            </div>

            <div className="relative -mx-4 pt-20 pb-32 md:mx-0 md:mt-18 md:pl-10 md:pt-44 md:pb-24 lg:pl-20">
              <div className="absolute top-0 left-0 h-full w-full bg-primary-100 md:w-[50vw]"></div>
              <div className="relative px-4 md:px-0">
                <h2>
                  <span className="text-gray-700">For Your</span>{" "}
                  <span className="text-primary-500">Business</span>
                </h2>
                <p className="text-gray-500">
                  Our CPAs in{" "}
                  {data.city.state
                    ? data.city.title + ", " + data.city.state
                    : data.city.title}{" "}
                  have extensive experience in strategic planning to help you
                  secure a bright future. And we deliver services that keep your
                  business on the right track to meet your goals.
                </p>

                <div className="mt-8 grid gap-y-5 gap-x-5 md:grid-cols-2">
                  <div className="space-y-5">
                    <Link
                      to="/business-tax-preparation-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="23"
                        height="23"
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 16.5532h3M4 3.07376V19.6639c0 .55.21071 1.0774.58579 1.4663.37507.389.88378.6074 1.41421.6074h12c.5304 0 1.0391-.2184 1.4142-.6074.3751-.3889.5858-.9163.5858-1.4663V7.57591c0-.27627-.0533-.54975-.1567-.80437-.1034-.25463-.2548-.48526-.4453-.67838l-4.44-4.50214c-.3736-.37881-.8754-.59095-1.398-.59102H6c-.53043 0-1.03914.21849-1.41421.60739C4.21071 1.9963 4 2.52377 4 3.07376v0zm5 9.33194h6-6z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                        <path
                          d="M14 .534424V4.68195c0 .55.2107 1.07747.5858 1.46637.3751.38891.8838.6074 1.4142.6074h4"
                          strokeWidth="2"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Business Tax Preparation
                      </div>
                    </Link>

                    <Link
                      to="/business-tax-planning-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="23"
                        height="27"
                        viewBox="0 0 23 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.8824 7.34138V5.91019c0-.13119-.1101-.23853-.2447-.23853H4.89344c-.13457 0-.24468.10734-.24468.23853v1.43119c0 .1312.11011.23854.24468.23854H16.6377c.1346 0 .2447-.10734.2447-.23854zM4.89344 9.96524c-.13457 0-.24468.10736-.24468.23856v1.4312c0 .1312.11011.2385.24468.2385h5.62746c.1346 0 .2447-.1073.2447-.2385v-1.4312c0-.1312-.1101-.23856-.2447-.23856H4.89344zM9.29753 23.4423H2.20205V2.45148H19.3291V12.7084c0 .1311.1101.2385.2446.2385h1.7127c.1346 0 .2447-.1074.2447-.2385V1.25882c0-.527756-.4373-.954132-.9787-.954132H.978687C.437351.304688 0 .731064 0 1.25882V24.635c0 .5277.437351.9541.978687.9541H9.29753c.13457 0 .24467-.1073.24467-.2385v-1.6698c0-.1312-.1101-.2385-.24467-.2385zm13.63127 1.5356l-2.8535-2.7819c.682-.8796 1.0888-1.9769 1.0888-3.1665 0-2.8982-2.41-5.2477-5.3828-5.2477-2.9727 0-5.3827 2.3495-5.3827 5.2477 0 2.8981 2.41 5.2477 5.3827 5.2477 1.0949 0 2.1103-.3191 2.9606-.8647l2.8963 2.8236c.0489.0477.1101.0686.1712.0686.0612 0 .1254-.0239.1713-.0686l.9481-.9243c.0226-.0219.0405-.0479.0527-.0766.0122-.0286.0185-.0593.0185-.0904 0-.031-.0063-.0617-.0185-.0904a.233891.233891 0 00-.0527-.0765zm-7.1475-2.609c-1.8931 0-3.4254-1.4938-3.4254-3.3394 0-1.8457 1.5323-3.3395 3.4254-3.3395 1.8932 0 3.4254 1.4938 3.4254 3.3395 0 1.8456-1.5322 3.3394-3.4254 3.3394z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Business Tax Planning
                      </div>
                    </Link>

                    <Link
                      to="/outsourced-accounting-bookkeeping-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="23"
                        height="25"
                        viewBox="0 0 23 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20.4444.492432H3.83333C2.29233.492432 0 1.45123 0 4.09243V20.8924c0 2.6412 2.29233 3.6 3.83333 3.6H23v-2.4H3.84867c-.59034-.0144-1.29311-.234-1.29311-1.2 0-.1212.0115-.2292.03066-.3276.14311-.69.74495-.8604 1.26117-.8724H21.7222c.023 0 .0396-.0108.0626-.012H23V2.89243c0-1.3236-1.1462-2.399998-2.5556-2.399998zm0 16.799968H2.55556V4.09243c0-.9672.70277-1.1856 1.27777-1.2h8.94447v8.39997l2.5555-1.2 2.5556 1.2V2.89243h2.5555V17.2924z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Accounting & Bookkeeping
                      </div>
                    </Link>

                    <Link
                      to="/business-consulting-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        width="25"
                        height="26"
                        viewBox="0 0 25 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.32598 24.0763c1.3923-.0002 2.74562-.4599 3.85012-1.3079 1.1045-.8479 1.8983-2.0366 2.2585-3.3819.3602-1.3452.2666-2.7717-.2663-4.0583-.533-1.2866-1.4754-2.3613-2.6813-3.0575-1.20577-.6962-2.60758-.975-3.98796-.7932-1.38038.1819-2.66219.8142-3.64666 1.799-.98447.9848-1.61657 2.267-1.79828 3.6477-.181701 1.3807.09714 2.7828.7933 3.9889L1.31725 23.76l2.84624-.5291c.96118.5558 2.05223.8475 3.16249.8454v0z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                        <path
                          d="M16.2751 19.358c1.1011-.1773 2.1605-.5546 3.1257-1.1135l4.14.7707-.7705-4.141c.6765-1.1738 1.0855-2.4827 1.1978-3.8329.1122-1.35031-.075-2.70873-.5483-3.97826-.4734-1.26953-1.2212-2.41884-2.19-3.36584-.9688-.94699-2.1348-1.66831-3.4145-2.11242a9.198159 9.198159 0 00-3.9889-.45672 9.198688 9.198688 0 00-3.8035 1.28599c-1.15769.70351-2.14579 1.65407-2.89371 2.78377-.74793 1.12969-1.23721 2.41065-1.4329 3.75138"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Business Consulting
                      </div>
                    </Link>
                  </div>

                  <div className="space-y-5">
                    {/* <Link
                      to="/business-valuation-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="23"
                        height="23"
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.5.869629c-1.1438 0-2.11865.884191-2.48842 2.095241H2.65385v2.09524h1.24377l-.24858.5238L.110577 12.9172 0 13.1792v.2619c0 2.8809 1.99038 5.2381 4.42308 5.2381 2.43269 0 4.42307-2.3572 4.42307-5.2381v-.2619l-.11057-.262-3.53846-7.33329-.24858-.5238h4.06392c.13299.43657.34556.83309.6226 1.16136.27704.32827.61174.58023.98034.73797V20.7744H7.07692v2.0952h8.84618v-2.0952h-3.5385V6.95839c.3681-.15876.7023-.41094.9794-.73887.277-.32792.4902-.72364.6244-1.15941h4.0631l-.2486.5238-3.5385 7.33329-.1106.262v.2619c0 2.8809 1.9904 5.2381 4.4231 5.2381 2.4327 0 4.4231-2.3572 4.4231-5.2381v-.2619l-.1106-.262-3.5384-7.33329-.2486-.5238h1.2438V2.96487h-6.3587C13.6187 1.75382 12.6447.869629 11.5.869629zm0 2.095241c.498 0 .8846.45885.8846 1.04762 0 .58981-.3874 1.04762-.8846 1.04762-.498 0-.8846-.45886-.8846-1.04762 0-.58981.3874-1.04762.8846-1.04762zM4.42308 8.23544l2.0178 4.15796H2.40615l2.01693-4.15796zm14.15382 0l2.0178 4.15796H16.56l2.0169-4.15796zM1.99038 14.4887h4.86539c-.35562 1.2152-1.27827 2.0952-2.43269 2.0952-1.15443 0-2.07708-.88-2.4327-2.0952zm14.15382 0h4.8654c-.3556 1.2152-1.2783 2.0952-2.4327 2.0952s-2.0771-.88-2.4327-2.0952z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Business Valuation
                      </div>
                    </Link> */}

                    <Link
                      to="/tax-audit-attestation-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="23"
                        height="23"
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.95366.476562c2.16724.000001 4.24564.860918 5.77804 2.393358 1.5325 1.53244 2.3934 3.61087 2.3934 5.77807 0 2.02401-.7417 3.88461-1.9612 5.31771l.3395.3394h.9931l6.2857 6.2857-1.8857 1.8858-6.2857-6.2858v-.9931l-.3394-.3394c-1.4828 1.2656-3.3683 1.961-5.31774 1.9611-2.1672 0-4.24564-.8609-5.77808-2.3933C1.64314 12.8936.782227 10.8152.782227 8.64799c0-2.1672.860913-4.24563 2.393353-5.77807C4.70802 1.33748 6.78646.476563 8.95366.476562zm0 2.514288c-3.14286 0-5.65715 2.51428-5.65715 5.65714 0 3.14281 2.51429 5.65711 5.65715 5.65711 3.14284 0 5.65714-2.5143 5.65714-5.65711 0-3.14286-2.5143-5.65714-5.65714-5.65714z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        Audit & Attestation Services
                      </div>
                    </Link>

                    <Link
                      to="/irs-representation-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="22"
                        height="20"
                        viewBox="0 0 22 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.30769 0C8.382 0 7.61538.787826 7.61538 1.73913v.86957H5.07692v-.86957h-1.6923v.86957H1.69231C.766615 2.6087 0 3.39652 0 4.34783V18.2609C0 19.2122.766615 20 1.69231 20H20.3077C21.2334 20 22 19.2122 22 18.2609V4.34783c0-.95131-.7666-1.73913-1.6923-1.73913h-1.6923v-.86957h-1.6923v.86957h-2.5385v-.86957C14.3846.787826 13.618 0 12.6923 0H9.30769zm0 1.73913h3.38461v.86957H9.30769v-.86957zm-7.61538 2.6087H20.3077V18.2609h-1.6923V5.21739h-1.6923V18.2609H5.07692V5.21739h-1.6923V18.2609H1.69231V4.34783z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-primary-500">
                        IRS Representation
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative mb-20 bg-secondary-100 md:mb-32 md:bg-transparent">
        <div className="container">
          <div className="grid md:grid-cols-2">
            <div className="relative order-2 -mx-4 pt-20 pb-32 md:order-1 md:mx-0 md:mb-18 md:pr-10 md:pt-40 md:pb-24 lg:pr-20">
              <div className="absolute top-0 right-0 h-full w-full bg-secondary-100 md:w-[50vw]"></div>
              <div className="relative px-4 md:px-0">
                <h2>
                  <span className="text-gray-700">For Your</span>{" "}
                  <span className="text-secondary-500">Personal</span> Finances
                </h2>
                <p className="text-gray-500">
                  Everyone can benefit from working with a good CPA. Our
                  experienced team can help you with:
                </p>

                <div className="mt-8 grid gap-y-5 gap-x-5 md:grid-cols-2">
                  <div className="space-y-5">
                    <Link
                      to="/personal-tax-preparation-planning-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="22"
                        height="29"
                        viewBox="0 0 22 29"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 17.3768h3M3 3.89536V20.488c0 .55.21071 1.0776.58579 1.4665.37507.389.88378.6075 1.41421.6075h12c.5304 0 1.0391-.2185 1.4142-.6075.3751-.3889.5858-.9165.5858-1.4665V8.39818c0-.27631-.0533-.54983-.1567-.80449s-.2548-.48534-.4453-.67847l-4.44-4.50282c-.3736-.37887-.8754-.59104-1.398-.59111H5c-.53043 0-1.03914.21852-1.41421.60748C3.21071 2.81774 3 3.34528 3 3.89536v0zm5 9.33334h6-6z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                        <path
                          d="M12 1.82129v4.14753c0 .54999.2107 1.07746.5858 1.46637.3751.38891.8838.60739 1.4142.60739h4"
                          strokeWidth="2"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        Tax Preparation & Planning
                      </div>
                    </Link>

                    <Link
                      to="/estate-trust-planning-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="24"
                        height="23"
                        viewBox="0 0 24 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.20117 19.9326V10.036h1.46655v9.8966c0 .2019.07725.3955.21477.5383.13751.1427.32402.2229.5185.2229H18.5999c.1945 0 .381-.0802.5185-.2229.1375-.1428.2147-.3364.2147-.5383V10.036h1.4666v9.8966c0 .6057-.2318 1.1866-.6443 1.6149-.4126.4283-.9721.6689-1.5555.6689H5.40099c-.58343 0-1.14296-.2406-1.55551-.6689-.41254-.4283-.64431-1.0092-.64431-1.6149v0zM19.3331 3.18461v5.3289l-2.933-3.04509V3.18461c0-.2019.0772-.39553.2147-.5383.1375-.14276.3241-.22297.5185-.22297h1.4666c.1945 0 .381.08021.5185.22297.1375.14277.2147.3364.2147.5383z"
                          stroke="gray"
                          strokeWidth=".5"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M10.9632 1.66209c.275-.28543.6479-.44578 1.0368-.44578.3889 0 .7618.16035 1.0368.44578L22.785 11.7809c.1376.1429.215.3368.215.539 0 .2021-.0774.396-.215.539-.1377.1429-.3245.2232-.5192.2232-.1947 0-.3815-.0803-.5192-.2232L12 2.73853 2.25335 12.8589c-.13768.1429-.32443.2232-.51915.2232s-.38147-.0803-.51916-.2232C1.07735 12.7159 1 12.522 1 12.3199c0-.2022.07735-.3961.21504-.539L10.9632 1.66209z"
                          strokeWidth=".5"
                          className="fill-current stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        Estate & Trust Planning
                      </div>
                    </Link>

                    <Link
                      to="/crypto-consulting-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="24"
                        height="26"
                        viewBox="0 0 24 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 24.9199c6.0753 0 11-5.1485 11-11.5 0-6.35143-4.9247-11.49998-11-11.49998S1 7.06847 1 13.4199c0 6.3515 4.9247 11.5 11 11.5z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                        <path
                          d="M7.6001 8.82002h6.6c.7337 0 2.2.46 2.2 2.29998 0 1.84-2.2 2.3-4.5 2.3m0 0c2.3 0 4.5.46 4.5 2.3 0 1.84-1.4663 2.3-2.2 2.3h-6.6m4.3-4.6h-2.1m0 0V6.52002m0 6.89998v6.9"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        Crypto Consulting
                      </div>
                    </Link>
                  </div>

                  <div className="space-y-5">
                    <Link
                      to="/family-owned-business-cpa/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="22"
                        height="24"
                        viewBox="0 0 22 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.9875 23.6895h-2.1c-.3712-.0005-.7271-.1677-.9895-.4651-.2625-.2974-.4101-.7006-.4105-1.1212v-3.9655h1.4v3.9655h2.1v-3.9655h1.4v-3.1724c-.0002-.2103-.074-.4119-.2052-.5606-.1313-.1487-.3092-.2323-.4948-.2325h-6.104l-1.4-4.75861h-4.396c-.18559.00021-.36353.08384-.49477.23253-.13124.14869-.20504.3503-.20523.56058v4.7586h1.4v7.1379h2.8v-5.5517h1.4v5.5517c-.00037.4206-.14799.8238-.41046 1.1212s-.61835.4646-.98954.4651h-2.8c-.37119-.0005-.72707-.1677-.98954-.4651s-.41009-.7006-.41046-1.1212v-5.5517c-.37119-.0004-.72707-.1676-.98954-.465-.262471-.2974-.410089-.7006-.41046-1.1212v-4.7586c.000556-.63084.221984-1.23566.61569-1.68174.39371-.44607.92753-.69694 1.48431-.69757h4.396c.2938-.00021.58019.10441.81855.29901.23835.19461.41656.46932.50935.78516L9.5915 12.586h5.096c.5568.0006 1.0906.2515 1.4843.6976.3937.4461.6151 1.0509.6157 1.6817v3.1724c-.0004.4206-.148.8238-.4105 1.1212-.2624.2974-.6183.4646-.9895.465v2.3793c-.0004.4206-.148.8238-.4105 1.1212-.2624.2974-.6183.4646-.9895.4651zm5.6 0h-1.4v-8.7242h2.1v-4.7586c-.0002-.21028-.074-.41189-.2052-.56058-.1313-.14869-.3092-.23232-.4948-.23253h-2.8v-1.5862h2.8c.5568.00063 1.0906.2515 1.4843.69757.3937.44608.6151 1.0509.6157 1.68174v4.7586c-.0004.4206-.148.8238-.4105 1.1212-.2624.2974-.6183.4646-.9895.465h-.7v7.138zm-14.7-16.65522c-.55379 0-1.09514-.18606-1.5556-.53465-.46045-.34859-.81934-.84405-1.03126-1.42373-.21193-.57968-.26738-1.21755-.15934-1.83294.10804-.61539.37471-1.18066.7663-1.62433S3.7981.87282 4.34125.750412C4.88439.628003 5.44738.690828 5.95901.93094c.51164.24011.94894.64673 1.2566 1.16843.30767.5217.47189 1.13505.47189 1.7625-.00093.84105-.29622 1.64736-.82112 2.24207-.52491.59472-1.23656.92929-1.97888.93034v0zm0-4.75862c-.27689 0-.54757.09303-.7778.26733-.23023.17429-.40967.42202-.51563.71186-.10596.28984-.13369.60878-.07967.91647.05402.3077.18736.59033.38315.81217.19579.22183.44525.3729.71682.43411.27158.0612.55307.02979.80889-.09027.25581-.12005.47446-.32336.6283-.58421.15383-.26085.23594-.56753.23594-.88125-.00037-.42056-.14799-.82378-.41046-1.12116s-.61835-.46463-.98954-.46505v0zm12.6 4.75862c-.5538 0-1.0951-.18606-1.5556-.53465-.4605-.34859-.8193-.84405-1.0313-1.42373-.2119-.57968-.2673-1.21755-.1593-1.83294.108-.61539.3747-1.18066.7663-1.62433s.8905-.74581 1.4336-.868218c.5432-.122409 1.1062-.059584 1.6178.180528.5116.24011.9489.64673 1.2566 1.16843s.4719 1.13505.4719 1.7625c-.0009.84105-.2962 1.64736-.8211 2.24207-.5249.59472-1.2366.92929-1.9789.93034v0zm0-4.75862c-.2769 0-.5476.09303-.7778.26733-.2302.17429-.4097.42202-.5156.71186-.106.28984-.1337.60878-.0797.91647.054.3077.1874.59033.3832.81217.1957.22183.4452.3729.7168.43411.2715.0612.553.02979.8089-.09027.2558-.12005.4744-.32336.6283-.58421.1538-.26085.2359-.56753.2359-.88125-.0004-.42056-.148-.82378-.4105-1.12116-.2624-.29738-.6183-.46463-.9895-.46505v0z"
                          strokeWidth=".5"
                          strokeLinejoin="round"
                          className="fill-current stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                        <path
                          d="M12.9373 11.7929c-.4846 0-.9582-.1628-1.3611-.4678-.4029-.305-.717-.7385-.9024-1.2457-.1854-.50727-.234-1.06541-.1394-1.60387.0945-.53846.3279-1.03308.6705-1.42129.3426-.38821.7792-.65258 1.2544-.75969.4753-.10711.9679-.05214 1.4156.15796.4477.2101.8303.56589 1.0995 1.02238.2692.45649.4129.99317.4129 1.54218-.0007.73595-.2591 1.44153-.7184 1.96193-.4593.5204-1.082.8131-1.7316.8139v0zm0-3.96548c-.2077 0-.4107.06977-.5833.20049-.1727.13072-.3073.31652-.3868.5339-.0794.21738-.1002.45658-.0597.68735.0405.23078.1405.44275.2873.60913.1469.16641.334.27971.5377.32561.2036.0459.4148.0223.6066-.0677.1919-.0901.3559-.24255.4712-.43819.1154-.19564.177-.42564.177-.66094-.0004-.31538-.1111-.61773-.3079-.84075-.1969-.22301-.4637-.34848-.7421-.3489v0z"
                          strokeWidth=".5"
                          strokeLinejoin="round"
                          className="fill-current stroke-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>

                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        Family Business
                      </div>
                    </Link>

                    <Link
                      to="/irs-representation-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="22"
                        height="21"
                        viewBox="0 0 22 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.30769.887695c-.92569 0-1.69231.787825-1.69231 1.739135v.86956H5.07692v-.86956h-1.6923v.86956H1.69231C.766615 3.49639 0 4.28422 0 5.23552V19.1486c0 .9513.766615 1.7391 1.69231 1.7391H20.3077c.9257 0 1.6923-.7878 1.6923-1.7391V5.23552c0-.9513-.7666-1.73913-1.6923-1.73913h-1.6923v-.86956h-1.6923v.86956h-2.5385v-.86956c0-.95131-.7666-1.739135-1.6923-1.739135H9.30769zm0 1.739135h3.38461v.86956H9.30769v-.86956zM1.69231 5.23552H20.3077V19.1486h-1.6923V6.10509h-1.6923V19.1486H5.07692V6.10509h-1.6923V19.1486H1.69231V5.23552z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        IRS Representation
                      </div>
                    </Link>

                    <Link
                      to="/wealth-management-services/"
                      className="group flex items-center space-x-3 no-underline"
                    >
                      <svg
                        className="min-w-[23px]"
                        width="22"
                        height="23"
                        viewBox="0 0 22 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.8205.194336c-.92573 0-1.69234.794691-1.69234 1.754284 0 .31577.09307.62014.23776.87714H4.05123v2.08322l.18531.21928 3.09354 3.8375c-.46539.0307-.82331.4491-.79369.93241.03046.48243.43323.85253.89946.82183h.55507l-1.32169 4.8796-.05331.1368-.55507 1.1236h-1.243c-.46539.0237-.82923.4351-.80639.9184.02285.4824.4197.8596.88593.8359l-1.53408 2.1104-.15823.2193V23H18.4358v-2.056l-.159-.2193-1.5333-2.1104c.3047.0035.5881-.1614.7438-.435.0747-.1345.114-.2869.114-.4421s-.0393-.3076-.114-.4421c-.1557-.2737-.44-.4386-.7438-.4351h-1.1634l-.5551-1.1236-.0271-.0552-.0254-.0816-1.3233-4.8796h.5559c.2243.0177.4462-.0574.6172-.209.1709-.1516.277-.3672.2949-.59968.0171-.23252-.0554-.46259-.2017-.63981-.1462-.17722-.3542-.28716-.5784-.30575l3.0673-3.8375.1853-.21928V2.82576H12.275c.1455-.257.2378-.56137.2378-.87714 0-.959593-.7666-1.754284-1.6923-1.754284zM6.00754 4.58005h9.62586l-3.5175 4.38571H9.52585L6.00754 4.58005zM9.76277 10.72h2.11543l1.507 5.5094.027.0824.264.5482H7.96469l.26485-.5482.02538-.0824L9.76277 10.72zm-2.75 7.8943h7.61543l1.9038 2.6315H5.10892l1.90385-2.6315z"
                          className="fill-current text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500"
                        />
                      </svg>
                      <div className="text-lg text-gray-500 transition-colors duration-300 ease-linear group-hover:text-secondary-500">
                        Wealth Management
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 -mx-4 md:order-2 md:mx-0 md:min-h-[847px]">
              <div className="h-full overflow-hidden rounded-bl-6xl md:absolute md:right-0 md:w-[50vw]">
                <StaticImage
                  src="../images/1.0 Homepage/4.0 Personal.jpg"
                  loading="lazy"
                  width={660}
                  className="h-full w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <About
        location={
          data.city.state
            ? data.city.title + ", " + data.city.state
            : data.city.title
        }
      />
      <RecentPosts />

      {data.city._rawWhyWeLoveText && data.city.whyWeLoveImage && (
        <section className="mb-20 md:mb-32">
          <div className="container">
            <div className="grid items-center gap-y-10 md:grid-cols-2 md:gap-x-10 lg:gap-x-20">
              <div>
                <GatsbyImage
                  image={data.city.whyWeLoveImage.asset.gatsbyImageData}
                  className="z-0 rounded-tl-6xl rounded-br-6xl"
                />
              </div>
              <div>
                <h2>
                  Your{" "}
                  {data.city.state
                    ? data.city.title + ", " + data.city.state
                    : data.city.title}{" "}
                  CPA Firm
                </h2>
                <PortableText blocks={data.city._rawWhyWeLoveText} />
              </div>
            </div>
          </div>
        </section>
      )}

      <CallToAction
        location={
          data.city.state
            ? data.city.title + ", " + data.city.state
            : data.city.title
        }
      />
    </Layout>
  );
};

export default Template;
